import { Ruler } from 'lucide-react';
import { IPostDetailResponse } from '@/redux/post/post.d';
import {
  SpecCard,
  SpecCaption,
  SpecRow,
  SpecPill,
  SpecDims,
} from './specCardParts';

const yesish = (value: string) => /^(yes|enabled|on|true)$/i.test(value.trim());

// Complexity is inherent to the spec table: one branch per optional job field.
// Carried in from in-flight work on this card, not introduced by the
// makes/comments redesign this commit is about — left for that author to split.
// fallow-ignore-next-line complexity
function JobDetailsCard({ post }: { post: IPostDetailResponse }) {
  const allTools = post.modelGroups?.flatMap((g) => g.toolData ?? []) ?? [];
  const toolType = allTools.some((t) => t.laserOrEndmill === 'Laser')
    ? 'Laser'
    : allTools.find((t) => t.laserOrEndmill)?.laserOrEndmill || 'Endmill';

  const dims = (
    [
      ['Height', post.jobSize?.height],
      ['Width', post.jobSize?.width],
      ['Thickness', post.jobSize?.thickness],
      ['Diameter', post.jobSize?.diameter],
      ['Length', post.jobSize?.length],
    ] as const
  )
    // These are numbers, so comparing to '' never narrowed anything — stringify
    // first so a blank value coming back from the API is still filtered out.
    .filter(([, v]) => v != null && String(v) !== '')
    .map(([label, value]) => ({
      label,
      value: String(value),
      unit: post.units,
    }));

  if (
    !post.toolSetter &&
    !post.probeForZHeightCommand &&
    !post.jobSize &&
    !post.modelGroups?.length
  ) {
    return null;
  }

  const hasJobSection =
    post.jobType ||
    dims.length > 0 ||
    (post.flipDirection && post.jobType === 'Double Sided');

  return (
    <SpecCard
      icon={<Ruler className="h-[18px] w-[18px]" />}
      title="Tool and Job Details"
    >
      <SpecCaption>Tooling</SpecCaption>
      <SpecRow label="Tool Type">{toolType}</SpecRow>
      {post.toolSetter && (
        <SpecRow label="Tool Setter">
          <SpecPill active={yesish(post.toolSetter)}>
            {post.toolSetter}
          </SpecPill>
        </SpecRow>
      )}
      {post.probeForZHeightCommand && (
        <SpecRow label="ATC">
          <SpecPill active={yesish(post.probeForZHeightCommand)}>
            {post.probeForZHeightCommand}
          </SpecPill>
        </SpecRow>
      )}

      {hasJobSection && (
        <SpecCaption>
          {post.jobType ? `Job — ${post.jobType}` : 'Job'}
        </SpecCaption>
      )}
      {post.flipDirection && post.jobType === 'Double Sided' && (
        <SpecRow label="Flip Direction">{post.flipDirection}</SpecRow>
      )}
      {dims.length > 0 && (
        <>
          <SpecCaption>Material Dimensions</SpecCaption>
          <SpecDims items={dims} />
        </>
      )}
    </SpecCard>
  );
}

export default JobDetailsCard;
